import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { JamRegistrationStatus } from "@/server/platform/db/schema/jam-registrations";
import { formatDateTime } from "@/shared/lib/date-time";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
    Badge,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Label,
    Textarea,
    confirm,
    toast,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, DownloadIcon, PhoneIcon, UserIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { canTransitionJamRegistrationStatus } from "../../domain/lib/status-transitions";
import type { JamSimilarStudentMatchDTO } from "../api/client";
import {
    useConvertJamRegistrationMutation,
    useJamEventQuery,
    useJamRegistrationQuery,
    useUpdateJamRegistrationMutation,
} from "../api/queries";
import {
    JAM_FORMAT_LABELS,
    JAM_REGISTRATION_STATUS_LABELS,
    JAM_REGISTRATION_STATUS_VARIANTS,
} from "../lib/jam-status";

const ACTION_STATUSES = [
  "contacted",
  "confirmed",
  "rejected",
  "duplicate",
] as const;

function MediaDownloadButton({
  url,
  label,
}: {
  url: string;
  label: string;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="mt-2 w-fit gap-1.5"
      onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
    >
      <DownloadIcon className="size-4 shrink-0" aria-hidden />
      {label}
    </Button>
  );
}

export function JamRegistrationDetailPage() {
  const { id } = useParams({ from: "/app/jam-registrations/$id" });
  const nav = useNavigate();
  const canUpdate = useActionPermission(["jam-registrations:update"]);
  const canConvert = useActionPermission(["jam-registrations:convert"]);
  const { data: reg, isLoading } = useJamRegistrationQuery(id);
  const { data: eventDetail } = useJamEventQuery(reg?.eventId);
  const updateMutation = useUpdateJamRegistrationMutation();
  const convertMutation = useConvertJamRegistrationMutation();
  const [adminNotes, setAdminNotes] = useState("");
  const [similarMatches, setSimilarMatches] = useState<
    JamSimilarStudentMatchDTO[] | null
  >(null);

  const sameFormatCategoryOptions = useMemo(() => {
    if (!reg?.category || !eventDetail?.categories) return [];
    const format = reg.category.format;
    return eventDetail.categories
      .filter((c) => c.format === format && (c.enabled || c.id === reg.categoryId))
      .map((c) => {
        const remainingLabel =
          c.remaining == null ? "" : ` · ເຫຼືອ ${c.remaining}`;
        const full = c.availability === "full" && c.id !== reg.categoryId;
        return {
          value: c.id,
          label: `${c.name}${remainingLabel}${full ? " (ເຕັມ)" : ""}`,
          disabled: full,
        };
      });
  }, [reg?.category, reg?.categoryId, eventDetail?.categories]);

  if (isLoading || !reg) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "Dashboard", href: "/app/dashboard" },
              { label: "ໃບສະໝັກ Jam", href: "/app/jam-registrations" },
              { label: "..." },
            ]}
          />
        </Header>
        <Main>
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
        </Main>
      </>
    );
  }

  const displayName = reg.teamName ?? reg.fullName;
  const canRunConvert =
    canConvert &&
    !reg.studentId &&
    reg.status !== "converted" &&
    (reg.status === "confirmed" || reg.status === "contacted");

  const handleStatus = async (status: JamRegistrationStatus) => {
    if (
      !canTransitionJamRegistrationStatus(
        reg.status as JamRegistrationStatus,
        status,
      )
    ) {
      toast.error("ບໍ່ສາມາດປ່ຽນສະຖານະໄດ້");
      return;
    }
    const ok = await confirm({
      title: JAM_REGISTRATION_STATUS_LABELS[status] ?? status,
      description: "ຢືນຢັນການປ່ຽນສະຖານະ?",
    });
    if (!ok) return;
    try {
      await updateMutation.mutateAsync({ id: reg.id, body: { status } });
      toast.success("ອັບເດດສະຖານະແລ້ວ");
    } catch {
      toast.error("ອັບເດດບໍ່ສຳເລັດ");
    }
  };

  const handleSaveNotes = async () => {
    try {
      await updateMutation.mutateAsync({
        id: reg.id,
        body: { adminNotes: adminNotes || null },
      });
      toast.success("ບັນທຶກໝາຍເຫດແລ້ວ");
    } catch {
      toast.error("ບັນທຶກບໍ່ສຳເລັດ");
    }
  };

  const handleChangeCategory = async (categoryId: string) => {
    if (!categoryId || categoryId === reg.categoryId || !reg.category) return;
    const target = eventDetail?.categories.find((c) => c.id === categoryId);
    const ok = await confirm({
      title: "ປ່ຽນໝວດໝູ່",
      description: target
        ? `ປ່ຽນເປັນ «${target.name}» — ໄດ້ສະເພາະຮູບແບບດຽວກັນ (${JAM_FORMAT_LABELS[reg.category.format]})`
        : "ຢືນຢັນການປ່ຽນໝວດໝູ່?",
    });
    if (!ok) return;
    try {
      await updateMutation.mutateAsync({
        id: reg.id,
        body: { categoryId },
      });
      toast.success("ປ່ຽນໝວດໝູ່ແລ້ວ");
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      if (message === "FORMAT_MISMATCH") {
        toast.error("ປ່ຽນໄດ້ສະເພາະໝວດ 1vs1 ↔ 1vs1 ຫຼື 2vs2 ↔ 2vs2");
      } else if (message === "CATEGORY_FULL") {
        toast.error("ໝວດເປົ້າໝາຍເຕັມແລ້ວ");
      } else if (message === "CATEGORY_RETIRED") {
        toast.error("ໝວດນີ້ປິດໃຊ້ງານແລ້ວ");
      } else {
        toast.error("ປ່ຽນໝວດໝູ່ບໍ່ສຳເລັດ");
      }
    }
  };

  const handleConvert = async (opts?: {
    linkExistingStudentId?: string;
    forceCreateNew?: boolean;
  }) => {
    try {
      const result = await convertMutation.mutateAsync({
        id: reg.id,
        body: opts ?? {},
      });
      setSimilarMatches(null);
      toast.success(
        result.linked
          ? `ຜູກກັບນັກຮຽນ ${result.memberId} ແລ້ວ`
          : `ສ້າງນັກຮຽນ ${result.memberId} ແລ້ວ`,
      );
    } catch (err) {
      const e = err as Error & {
        code?: string;
        matches?: JamSimilarStudentMatchDTO[];
      };
      if (e.code === "SIMILAR_STUDENTS" && e.matches?.length) {
        setSimilarMatches(e.matches);
        return;
      }
      toast.error("Convert ບໍ່ສຳເລັດ");
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "Dashboard", href: "/app/dashboard" },
            { label: "ໃບສະໝັກ Jam", href: "/app/jam-registrations" },
            { label: displayName },
          ]}
        />
      </Header>
      <Main>
        <Button
          variant="ghost"
          className="mb-4"
          onClick={() => nav({ to: "/app/jam-registrations" })}
        >
          <ArrowLeftIcon className="mr-2 size-4" />
          ກັບໄປລາຍການ
        </Button>

        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle>{displayName}</CardTitle>
                <Badge
                  variant={
                    JAM_REGISTRATION_STATUS_VARIANTS[reg.status] ?? "outline"
                  }
                >
                  {JAM_REGISTRATION_STATUS_LABELS[reg.status] ?? reg.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 text-sm">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <p className="text-muted-foreground text-xs">ໝວດໝູ່</p>
                  {canUpdate && reg.category && sameFormatCategoryOptions.length > 0 ? (
                    <div className="mt-1 space-y-1">
                      <SimpleSelect
                        value={reg.categoryId}
                        onValueChange={(v) => {
                          void handleChangeCategory(v);
                        }}
                        disabled={updateMutation.isPending}
                        options={sameFormatCategoryOptions}
                        className="max-w-md"
                      />
                      <p className="text-muted-foreground text-xs">
                        ປ່ຽນໄດ້ສະເພາະຮູບແບບດຽວກັນ ·{' '}
                        {JAM_FORMAT_LABELS[reg.category.format]}
                      </p>
                    </div>
                  ) : (
                    <>
                      <p className="font-medium">{reg.category?.name ?? "—"}</p>
                      {reg.category ? (
                        <p className="text-muted-foreground text-xs">
                          {JAM_FORMAT_LABELS[reg.category.format]}
                        </p>
                      ) : null}
                    </>
                  )}
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">ວັນທີສະໝັກ</p>
                  <p>{formatDateTime(reg.submittedAt)}</p>
                </div>
                <div className="flex items-start gap-2">
                  <PhoneIcon className="mt-0.5 size-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground text-xs">ເບີໂທ</p>
                    <p>{reg.phoneNumber}</p>
                  </div>
                </div>
                {reg.photoKey ? (
                  <div className="sm:col-span-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-muted-foreground text-xs">
                        {reg.category?.format === "duo"
                          ? "ຮູບທີມ (ໂປຣໂມກ)"
                          : "ຮູບນັກແຂ່ງ (ໂປຣໂມກ)"}
                      </p>
                      {reg.photoDownloadUrl ? (
                        <MediaDownloadButton
                          url={reg.photoDownloadUrl}
                          label="ດາວນໂຫຼດຮູບ"
                        />
                      ) : null}
                    </div>
                    {reg.photoReadUrl ? (
                      <img
                        src={reg.photoReadUrl}
                        alt=""
                        className="mt-2 max-h-64 w-full max-w-sm rounded-lg border object-cover"
                      />
                    ) : (
                      <p className="text-muted-foreground text-xs">
                        ບໍ່ສາມາດໂຫຼດຮູບໄດ້
                      </p>
                    )}
                  </div>
                ) : null}
                {reg.videoKey ? (
                  <div className="sm:col-span-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-muted-foreground text-xs">ວິດີໂອນັກແຂ່ງ</p>
                      {reg.videoDownloadUrl ? (
                        <MediaDownloadButton
                          url={reg.videoDownloadUrl}
                          label="ດາວນໂຫຼດວິດີໂອ"
                        />
                      ) : null}
                    </div>
                    {reg.videoReadUrl ? (
                      <video
                        src={reg.videoReadUrl}
                        controls
                        className="mt-2 max-h-64 w-full rounded-lg border bg-black/5"
                        preload="metadata"
                      />
                    ) : (
                      <p className="text-muted-foreground text-xs">
                        ບໍ່ສາມາດໂຫຼດວິດີໂອໄດ້
                      </p>
                    )}
                    {reg.videoDurationSeconds ? (
                      <p className="text-muted-foreground mt-1 text-xs">
                        ຄວາມຍາວ {reg.videoDurationSeconds} ວິນາທີ
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {reg.teamName ? (
                  <>
                    <div>
                      <p className="text-muted-foreground text-xs">ຊື່ທີມ</p>
                      <p>{reg.teamName}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">ສະມາຊິກທີ 1</p>
                      <p>{reg.fullName}</p>
                      {reg.nickname ? (
                        <p className="text-muted-foreground text-xs">
                          AKA: {reg.nickname}
                        </p>
                      ) : null}
                    </div>
                    {reg.partnerFullName ? (
                      <div>
                        <p className="text-muted-foreground text-xs">ສະມາຊິກທີ 2</p>
                        <p>{reg.partnerFullName}</p>
                        {reg.partnerNickname ? (
                          <p className="text-muted-foreground text-xs">
                            AKA: {reg.partnerNickname}
                          </p>
                        ) : null}
                        {reg.partnerPhone ? (
                          <p className="text-muted-foreground text-xs">
                            {reg.partnerPhone}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="flex items-start gap-2">
                    <UserIcon className="mt-0.5 size-4 text-muted-foreground" />
                    <div>
                      <p className="text-muted-foreground text-xs">ຊື່</p>
                      <p>{reg.fullName}</p>
                      {reg.nickname ? (
                        <p className="text-muted-foreground text-xs">
                          {reg.nickname}
                        </p>
                      ) : null}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col gap-4">
            {canUpdate ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">ຈັດການສະຖານະ</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  {ACTION_STATUSES.map((status) => (
                    <Button
                      key={status}
                      size="sm"
                      variant={reg.status === status ? "default" : "outline"}
                      disabled={reg.status === status}
                      onClick={() => handleStatus(status)}
                    >
                      {JAM_REGISTRATION_STATUS_LABELS[status]}
                    </Button>
                  ))}
                </CardContent>
              </Card>
            ) : null}

            {canRunConvert ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Convert → Student</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-2">
                  <p className="text-muted-foreground text-xs">
                    Preview: {reg.previewStudentName}
                  </p>
                  <Button
                    size="sm"
                    onClick={() => handleConvert()}
                    disabled={convertMutation.isPending}
                  >
                    Convert
                  </Button>
                  {similarMatches?.length ? (
                    <div className="mt-2 flex flex-col gap-2">
                      <p className="text-xs font-medium">ພົບເບີຊ້ຳ — ເລືອກຜູກກັບ:</p>
                      {similarMatches.map((m) => (
                        <Button
                          key={m.id}
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            handleConvert({ linkExistingStudentId: m.id })
                          }
                        >
                          {m.memberId} — {m.name}
                        </Button>
                      ))}
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleConvert({ forceCreateNew: true })}
                      >
                        ສ້າງນັກຮຽນໃໝ່
                      </Button>
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            ) : null}

            {canUpdate ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">ໝາຍເຫດ Admin</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-2">
                  <Label htmlFor="adminNotes">ໝາຍເຫດ</Label>
                  <Textarea
                    id="adminNotes"
                    defaultValue={reg.adminNotes ?? ""}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    rows={4}
                  />
                  <Button size="sm" onClick={handleSaveNotes}>
                    ບັນທຶກ
                  </Button>
                </CardContent>
              </Card>
            ) : null}
          </div>
        </div>
      </Main>
    </>
  );
}
